/**
 * @name ErrorPage
 * @description Página de erro geral da aplicação
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import GradientBackground from '@/components/common/backgrounds/GradientBackground';
import useThemeContext from '@/themes/hooks/useThemeContext';
import { Error as ErrorIcon, Home as HomeIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, Typography } from '@mui/material';
import React from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

const ErrorPage: React.FC = () => {
     const { theme } = useThemeContext();
     const navigate = useNavigate();
     const error = useRouteError() as any;

     const handleGoHome = () => {
          navigate('/', { replace: true });
     };

     const handleRefresh = () => {
          window.location.reload();
     };

     return (
          <GradientBackground>
               <Container maxWidth='md'>
                    <Box
                         sx={{
                              minHeight: '100vh',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 2,
                         }}
                    >
                         <Card
                              sx={{
                                   maxWidth: 600,
                                   textAlign: 'center',
                                   backgroundColor: theme.palette.background.paper,
                                   border: `1px solid ${theme.palette.divider}`,
                              }}
                         >
                              <CardContent sx={{ p: 4 }}>
                                   <ErrorIcon
                                        sx={{
                                             fontSize: 80,
                                             color: theme.palette.error.main,
                                             mb: 2,
                                        }}
                                   />

                                   <Typography
                                        variant='h4'
                                        component='h1'
                                        gutterBottom
                                   >
                                        Oops! Algo deu errado
                                   </Typography>

                                   <Typography
                                        variant='body1'
                                        color='text.secondary'
                                        sx={{ mb: 3 }}
                                   >
                                        Encontramos um erro inesperado. Nossa equipe foi notificada e está trabalhando para resolver o problema.
                                   </Typography>

                                   {error && (
                                        <Box
                                             sx={{
                                                  mb: 3,
                                                  p: 2,
                                                  backgroundColor: theme.palette.grey[100],
                                                  borderRadius: 1,
                                                  border: `1px solid ${theme.palette.grey[300]}`,
                                             }}
                                        >
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  <strong>Detalhes do erro:</strong>
                                             </Typography>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                                  sx={{ mt: 1 }}
                                             >
                                                  {error.statusText || error.message || 'Erro desconhecido'}
                                             </Typography>
                                        </Box>
                                   )}

                                   <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <Button
                                             variant='contained'
                                             startIcon={<HomeIcon />}
                                             onClick={handleGoHome}
                                             sx={{
                                                  backgroundColor: theme.palette.primary.main,
                                                  '&:hover': {
                                                       backgroundColor: theme.palette.primary.dark,
                                                  },
                                             }}
                                        >
                                             Voltar ao Início
                                        </Button>

                                        <Button
                                             variant='outlined'
                                             startIcon={<RefreshIcon />}
                                             onClick={handleRefresh}
                                             sx={{
                                                  borderColor: theme.palette.primary.main,
                                                  color: theme.palette.primary.main,
                                                  '&:hover': {
                                                       borderColor: theme.palette.primary.dark,
                                                       backgroundColor: theme.palette.primary.light + '20',
                                                  },
                                             }}
                                        >
                                             Tentar Novamente
                                        </Button>
                                   </Box>
                              </CardContent>
                         </Card>
                    </Box>
               </Container>
          </GradientBackground>
     );
};

export { ErrorPage };
